/**
 * Module dependencies.
 */

import React, { useEffect, useRef } from 'react';
import { Menu } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { MenuItem } from 'app/types/navigation';
import { useCollapseMenuProvider } from 'app/components/providers/collapse-menu-provider/collapse-menu-provider';
import { cn } from 'app/lib/utils';
import { appRoutes } from 'app/routes';
import { AppSidebar } from './app-shell/app-sidebar';
import { Breadcrumb } from './app-shell/breadcrumb';
import { BreadcrumbProvider } from './app-shell/breadcrumb-provider';
import { ImpersonationBanner } from './app-shell/impersonation-banner';

/**
 * `Props` type.
 */

type Props = {
  menu: MenuItem[];
  children: JSX.Element;
};

/**
 * Export `Dashboard` component.
 */

export function Dashboard({ menu, children }: Props): JSX.Element {
  const { collapsed, setCollapsed } = useCollapseMenuProvider();
  const { pathname } = useLocation();
  const mainRef = useRef<HTMLElement>(null);
  // The dashboard home has no breadcrumb, so drop the empty header bar on desktop
  // (mobile keeps it for the menu toggle).
  const isHome = pathname === appRoutes.dashboard.home.base || pathname === appRoutes.dashboard.base;

  // The scroll container is <main> (not the window), so reset it on navigation —
  // otherwise a new page opens at the previous page's scroll position.
  useEffect(() => {
    mainRef.current?.scrollTo(0, 0);
  }, [pathname]);

  return (
    <div className={'flex h-screen overflow-hidden bg-background text-foreground'}>
      {/* Mobile backdrop */}
      {collapsed && (
        <div
          aria-hidden={'true'}
          className={'fixed inset-0 z-40 bg-black/30 md:hidden'}
          onClick={() => setCollapsed(false)}
        />
      )}

      <AppSidebar menu={menu} mobileOpen={collapsed} onNavigate={() => setCollapsed(false)} />

      <div className={'flex min-w-0 flex-1 flex-col'}>
        <main className={'flex-1 overflow-x-hidden overflow-y-scroll bg-muted [scrollbar-gutter:stable]'} ref={mainRef}>
          <BreadcrumbProvider>
            <div className={'w-full py-6 pl-4 pr-6 md:pl-6 md:pr-10'}>
              <ImpersonationBanner />

              <div className={'rounded-xl border border-border bg-card shadow-sm'}>
                <div
                  className={cn(
                    'flex items-center gap-2.5 border-b border-border px-5 py-3.5 md:px-6',
                    isHome && 'md:hidden'
                  )}
                >
                  <button
                    className={
                      '-ml-1 grid h-8 w-8 place-items-center rounded-md text-muted-foreground hover:bg-muted md:hidden'
                    }
                    onClick={() => setCollapsed(!collapsed)}
                    type={'button'}
                  >
                    <Menu className={'h-5 w-5'} />
                  </button>

                  <Breadcrumb menu={menu} />
                </div>

                <div className={'px-5 py-5 md:px-6'}>{children}</div>
              </div>
            </div>
          </BreadcrumbProvider>
        </main>
      </div>
    </div>
  );
}
